import { useEffect, useMemo, useState, useRef, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Loader2, ArrowLeft, Save, Monitor, Smartphone, LayoutTemplate, Settings2, Trash2, Rocket, RefreshCw, Undo2, Redo2, GripVertical } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'sonner';
import { themeApi, type StoreTheme, type ThemeAsset } from './themeApi';
import { MediaManagerModal } from '../../../components/modals/MediaManagerModal';
import { ResourcePickerModal } from '../../../components/modals/ResourcePickerModal';
import { FontPicker } from '../../../components/ui/font-picker';
import { useBuilderState, type BuilderState } from './useBuilderState';

type DeviceMode = 'desktop' | 'mobile';
type ActiveTab = 'sections' | 'settings';

interface BuilderBlock {
  id: string;
  type: string;
  settings: Record<string, any>;
}

interface BuilderSection {
  id: string;
  type: string;
  settings: Record<string, any>;
  blocks: Record<string, BuilderBlock>;
  block_order: string[];
}

interface SectionSchema {
  name?: string;
  settings?: SchemaSetting[];
  blocks?: SchemaBlock[];
}

interface SchemaBlock {
  type: string;
  name: string;
  settings?: SchemaSetting[];
}

interface SchemaSetting {
  type: string;
  id: string;
  label?: string;
  default?: any;
  options?: Array<{ label?: string; value: string } | string>;
}

type PickerState = {
  type: 'image_picker' | 'product' | 'category';
  currentValue: string;
  onSelect: (val: string) => void;
} | null;

export default function VisualBuilder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deviceMode, setDeviceMode] = useState<DeviceMode>('desktop');
  const [activeTab, setActiveTab] = useState<ActiveTab>('sections');
  const [templateType, setTemplateType] = useState('index');
  const [previewVersion, setPreviewVersion] = useState(0);
  const [activePicker, setActivePicker] = useState<PickerState>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const hydratedTemplates = useRef<Set<string>>(new Set());

  const { data: storeTheme, isLoading } = useQuery({
    queryKey: ['store-themes', id],
    queryFn: () => themeApi.getInstalled(id as string),
    enabled: Boolean(id),
  });

  const sectionAssets = useMemo(
    () => (storeTheme?.assets ?? []).filter((asset) => asset.key.startsWith('sections/') && asset.key.endsWith('.liquid')),
    [storeTheme],
  );

  const template = storeTheme?.pageTemplates?.find((item) => item.templateType === templateType);
  const initialSections = useMemo(
    () => hydrateSections(template?.sectionsData, sectionAssets),
    [template?.sectionsData, sectionAssets],
  );

  const handleSave = async (stateToSave: BuilderState) => {
    await Promise.all([
      themeApi.saveTemplate(id as string, templateType, {
        sections: Object.fromEntries(
          stateToSave.sections.map((section) => [
            section.id,
            { type: section.type, settings: section.settings, blocks: section.blocks, block_order: section.block_order },
          ]),
        ),
        order: stateToSave.sections.map((section) => section.id),
      }),
      themeApi.saveSettings(id as string, stateToSave.settingsData)
    ]);
    queryClient.invalidateQueries({ queryKey: ['store-themes', id] });
    setPreviewVersion((v) => v + 1);
  };

  const {
    state,
    setState,
    undo,
    redo,
    canUndo,
    canRedo,
    saveStatus,
    retrySave,
    resetState,
  } = useBuilderState({
    initialState: { sections: [], settingsData: {} },
    templateType,
    onSave: handleSave,
  });

  const sections = state.sections;
  const settingsData = state.settingsData;

  useEffect(() => {
    if (!storeTheme) return;
    
    // Only hydrate each template once per session to avoid wiping out the undo/redo stack
    // when the theme is refetched (e.g. after a save). Keyed by theme ID and template type.
    const hydrationKey = `${storeTheme.id}::${templateType}`;
    if (!hydratedTemplates.current.has(hydrationKey)) {
      resetState({
        sections: initialSections,
        settingsData: (storeTheme.settingsData as Record<string, any>) ?? {},
      });
      hydratedTemplates.current.add(hydrationKey);
    }
  }, [storeTheme?.id, templateType, initialSections, resetState]);

  // Iframe Bidirectional Syncing
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate origin against the preview URL origin
      try {
        const expectedOrigin = new URL(themeApi.previewUrl(id as string, templateType)).origin;
        if (event.origin !== expectedOrigin) return;
        
        if (event.data?.type === 'SECTION_CLICKED' && event.data?.sectionId) {
          setActiveTab('sections');
          // In a real implementation, we would scroll the sidebar to this section ID
          console.log('Iframe requested section activation:', event.data.sectionId);
        }
      } catch (err) {
        // invalid URL parsing
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [id, templateType]);

  const publishMutation = useMutation({
    mutationFn: () => themeApi.publish(id as string),
    onSuccess: () => {
      toast.success('Theme published');
      queryClient.invalidateQueries({ queryKey: ['store-themes'] });
      queryClient.invalidateQueries({ queryKey: ['store-themes', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to publish theme');
    },
  });

  if (isLoading) {
    return <div className="flex h-screen w-full items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  if (!storeTheme) {
    return <div className="p-8">Theme not found</div>;
  }

  const previewUrl = `${themeApi.previewUrl(id as string, templateType)}&v=${previewVersion}`;
  const saving = saveStatus === 'saving';

  return (
      <div className="fixed inset-0 z-[60] flex flex-col bg-background">
      {activePicker?.type === 'image_picker' && (
        <MediaManagerModal
          isOpen={true}
          onClose={() => setActivePicker(null)}
          onSelect={(url) => {
            activePicker.onSelect(url);
            setActivePicker(null);
          }}
        />
      )}
      {(activePicker?.type === 'product' || activePicker?.type === 'category') && (
        <ResourcePickerModal
          isOpen={true}
          onClose={() => setActivePicker(null)}
          resourceType={activePicker.type}
          onSelect={(id) => {
            activePicker.onSelect(id);
            setActivePicker(null);
          }}
        />
      )}
      <header className="flex h-14 shrink-0 items-center justify-between border-b bg-card px-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/store/themes')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="text-sm font-semibold">{storeTheme.theme.name}</div>
            <div className="text-xs text-muted-foreground">{storeTheme.role.toLowerCase()} theme</div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={templateType}
            onChange={(e) => setTemplateType(e.target.value)}
            className="h-9 rounded-md border bg-background px-3 text-sm"
          >
            {templateOptions(storeTheme).map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          <div className="flex items-center rounded-md border bg-muted/50 p-1">
            <Button variant={deviceMode === 'desktop' ? 'secondary' : 'ghost'} size="sm" className="h-7 px-2" onClick={() => setDeviceMode('desktop')}>
              <Monitor className="h-4 w-4" />
            </Button>
            <Button variant={deviceMode === 'mobile' ? 'secondary' : 'ghost'} size="sm" className="h-7 px-2" onClick={() => setDeviceMode('mobile')}>
              <Smartphone className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center gap-1 mx-2">
            <Button variant="ghost" size="icon" onClick={undo} disabled={!canUndo} title="Undo (Cmd+Z)">
              <Undo2 className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={redo} disabled={!canRedo} title="Redo (Cmd+Shift+Z)">
              <Redo2 className="h-4 w-4" />
            </Button>
          </div>
          <Button variant="outline" onClick={() => setPreviewVersion((v) => v + 1)}>
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
          
          {saveStatus === 'error' ? (
            <Button onClick={retrySave} variant="destructive">
              <RefreshCw className="mr-2 h-4 w-4" /> Retry Save
            </Button>
          ) : (
            <Button onClick={() => handleSave(state)} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {saveStatus === 'saved' ? 'Saved' : 'Save'}
            </Button>
          )}
          <Button variant="outline" onClick={() => publishMutation.mutate()} disabled={publishMutation.isPending}>
            <Rocket className="mr-2 h-4 w-4" /> Publish
          </Button>
        </div>
      </header>

      <div className="flex-1 flex w-full h-full overflow-hidden">
        <div className="w-[320px] shrink-0 flex flex-col border-r bg-white h-full relative z-20 shadow-xl">
          <div className="flex border-b">
            <TabButton active={activeTab === 'sections'} onClick={() => setActiveTab('sections')} icon={<LayoutTemplate className="h-4 w-4" />} label="Sections" />
            <TabButton active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} icon={<Settings2 className="h-4 w-4" />} label="Settings" />
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {activeTab === 'sections' ? (
              <SectionsPanel
                sections={sections}
                sectionAssets={sectionAssets}
                onAdd={(type) => setState((prev) => ({ ...prev, sections: [...prev.sections, newSection(type, sectionSchemaFor(sectionAssets, type))] }))}
                onRemove={(sectionId) => setState((prev) => ({ ...prev, sections: prev.sections.filter((section) => section.id !== sectionId) }))}
                onChange={(sectionId, updatedSection) =>
                  setState((prev) => ({ ...prev, sections: prev.sections.map((section) => section.id === sectionId ? updatedSection : section) }))
                }
                onOpenPicker={(type, currentValue, onSelect) => setActivePicker({ type, currentValue, onSelect })}
                onSectionFocus={(sectionId) => {
                  try {
                    const origin = new URL(previewUrl).origin;
                    iframeRef.current?.contentWindow?.postMessage({
                      type: 'SCROLL_TO_SECTION',
                      sectionId
                    }, origin);
                  } catch (e) {}
                }}
                onReorderSections={(newSections) => setState((prev) => ({ ...prev, sections: newSections }))}
                onReorderBlocks={(sectionId, newBlockOrder) => 
                  setState((prev) => ({
                    ...prev,
                    sections: prev.sections.map((section) => 
                      section.id === sectionId ? { ...section, block_order: newBlockOrder } : section
                    )
                  }))
                }
              />
            ) : (
              <SettingsPanel storeTheme={storeTheme} settingsData={settingsData} onChange={(newSettings) => setState(prev => ({ ...prev, settingsData: newSettings }))} onOpenPicker={(type, currentValue, onSelect) => setActivePicker({ type, currentValue, onSelect })} />
            )}
          </div>
        </div>
        
        <div className="w-1 bg-border transition-colors z-10" />

        <div className="flex-1 flex justify-center bg-muted/30 p-4 h-full overflow-y-auto min-w-0">
          <div className={deviceMode === 'mobile' ? 'my-auto h-[812px] w-[375px] shrink-0 overflow-hidden rounded-[2.5rem] bg-white shadow-xl ring-8 ring-zinc-800' : 'h-full w-full overflow-hidden rounded-md border bg-white shadow-sm'}>
            <iframe ref={iframeRef} src={previewUrl} className="h-full w-full border-0" title="Theme Preview" />
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionsPanel({
  sections,
  sectionAssets,
  onAdd,
  onRemove,
  onChange,
  onOpenPicker,
  onSectionFocus,
  onReorderSections,
  onReorderBlocks,
}: {
  sections: BuilderSection[];
  sectionAssets: ThemeAsset[];
  onAdd: (type: string) => void;
  onRemove: (sectionId: string) => void;
  onChange: (sectionId: string, section: BuilderSection) => void;
  onOpenPicker: (type: 'image_picker' | 'product' | 'category', currentValue: string, onSelect: (val: string) => void) => void;
  onSectionFocus?: (sectionId: string) => void;
  onReorderSections: (newSections: BuilderSection[]) => void;
  onReorderBlocks: (sectionId: string, newOrder: string[]) => void;
}) {
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      const activeType = String(active.id).startsWith('block:') ? 'block' : 'section';
      const overType = String(over.id).startsWith('block:') ? 'block' : 'section';

      if (activeType === 'section' && overType === 'section') {
        const oldIndex = sections.findIndex((s) => s.id === active.id);
        const newIndex = sections.findIndex((s) => s.id === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          onReorderSections(arrayMove(sections, oldIndex, newIndex));
        }
      } else if (activeType === 'block' && overType === 'block') {
        const activeParts = String(active.id).split(':');
        const overParts = String(over.id).split(':');
        if (activeParts[1] === overParts[1]) {
          const sectionId = activeParts[1];
          const section = sections.find(s => s.id === sectionId);
          if (section) {
            const oldIndex = section.block_order.indexOf(activeParts[2]);
            const newIndex = section.block_order.indexOf(overParts[2]);
            if (oldIndex !== -1 && newIndex !== -1) {
              onReorderBlocks(sectionId, arrayMove(section.block_order, oldIndex, newIndex));
            }
          }
        }
      }
    }
  };

  const activeSection = sections.find(s => s.id === activeSectionId);

  if (activeSection) {
    const schema = sectionSchemaFor(sectionAssets, activeSection.type);
    
    return (
      <div className="flex flex-col h-full animate-in slide-in-from-right-4 duration-200">
        <div className="flex items-center gap-2 mb-4 pb-4 border-b">
          <Button variant="ghost" size="icon" onClick={() => setActiveSectionId(null)} className="shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold truncate">{schema.name ?? activeSection.type}</span>
            <span className="text-[10px] text-muted-foreground font-mono truncate">{activeSection.id}</span>
          </div>
        </div>

        <div className="space-y-6 flex-1 overflow-y-auto pr-1 pb-10">
          <SchemaFields
            settings={schema.settings ?? []}
            values={activeSection.settings}
            onChange={(settings) => onChange(activeSection.id, { ...activeSection, settings })}
            onOpenPicker={onOpenPicker}
          />

          {schema.blocks && schema.blocks.length > 0 && (
            <div className="mt-6 pt-6 border-t border-dashed">
              <div className="text-sm font-semibold mb-3">Blocks</div>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={activeSection.block_order.map(id => `block:${activeSection.id}:${id}`)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2 mb-4">
                    {activeSection.block_order.map((blockId) => (
                      <SortableBlock
                        key={blockId}
                        sectionId={activeSection.id}
                        blockId={blockId}
                        block={activeSection.blocks[blockId]}
                        schema={schema}
                        onRemove={() => {
                          const newBlocks = { ...activeSection.blocks };
                          delete newBlocks[blockId];
                          onChange(activeSection.id, {
                            ...activeSection,
                            blocks: newBlocks,
                            block_order: activeSection.block_order.filter(id => id !== blockId)
                          });
                        }}
                        onChange={(newSettings) => onChange(activeSection.id, {
                          ...activeSection,
                          blocks: {
                            ...activeSection.blocks,
                            [blockId]: { ...activeSection.blocks[blockId], settings: newSettings }
                          }
                        })}
                        onOpenPicker={onOpenPicker}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              <select 
                className="h-9 w-full rounded-md border bg-background px-3 text-sm shadow-sm" 
                onChange={(e) => {
                  if (e.target.value) {
                    const blockType = e.target.value;
                    const blockSchema = schema.blocks?.find(b => b.type === blockType);
                    if (blockSchema) {
                      const blockId = `${blockType}-${Date.now().toString(36)}`;
                      onChange(activeSection.id, {
                        ...activeSection,
                        blocks: {
                          ...activeSection.blocks,
                          [blockId]: {
                            id: blockId,
                            type: blockType,
                            settings: Object.fromEntries((blockSchema.settings ?? []).filter(s => s.id).map(s => [s.id, s.default ?? '']))
                          }
                        },
                        block_order: [...activeSection.block_order, blockId]
                      });
                    }
                    e.target.value = '';
                  }
                }}
                value=""
              >
                <option value="">+ Add block</option>
                {schema.blocks.map(b => <option key={b.type} value={b.type}>{b.name ?? b.type}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-4">
        <select className="h-9 min-w-0 flex-1 rounded-md border bg-background px-3 text-sm shadow-sm" onChange={(e) => { if (e.target.value) { onAdd(e.target.value); e.target.value = ''; } }} value="">
          <option value="">Add section...</option>
          {sectionAssets.map((asset) => {
            const type = sectionTypeFromKey(asset.key);
            const schema = schemaFromAsset(asset);
            return <option key={asset.key} value={type}>{schema.name ?? type}</option>;
          })}
        </select>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2 flex-1 overflow-y-auto pb-10 pr-1">
            {sections.map((section) => (
              <SortableSectionListItem
                key={section.id}
                section={section}
                sectionAssets={sectionAssets}
                onRemove={() => onRemove(section.id)}
                onClick={() => {
                  setActiveSectionId(section.id);
                  onSectionFocus?.(section.id);
                }}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function SortableSectionListItem({
  section,
  sectionAssets,
  onRemove,
  onClick,
}: {
  section: BuilderSection;
  sectionAssets: ThemeAsset[];
  onRemove: () => void;
  onClick: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: section.id, data: { type: 'section' } });
  const schema = sectionSchemaFor(sectionAssets, section.type);
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div 
      ref={setNodeRef} 
      style={style}
      className="flex items-center justify-between rounded-lg border bg-card p-3 shadow-sm transition-all hover:border-primary/50 cursor-pointer group"
      onClick={(e) => {
        if (!(e.target as HTMLElement).closest('button')) {
          onClick();
        }
      }}
    >
      <div className="flex items-center gap-3 overflow-hidden">
        <div {...attributes} {...listeners} className="cursor-grab hover:text-primary text-muted-foreground transition-colors">
          <GripVertical className="h-4 w-4" />
        </div>
        <div className="flex flex-col overflow-hidden">
          <span className="text-sm font-semibold truncate">{schema.name ?? section.type}</span>
        </div>
      </div>
      <Button size="icon" variant="ghost" onClick={onRemove} className="opacity-0 group-hover:opacity-100 h-7 w-7 transition-opacity">
        <Trash2 className="h-4 w-4 text-red-500 hover:text-red-600" />
      </Button>
    </div>
  );
}

function SortableBlock({
  sectionId,
  blockId,
  block,
  schema,
  onRemove,
  onChange,
  onOpenPicker,
}: {
  sectionId: string;
  blockId: string;
  block: BuilderBlock;
  schema: SectionSchema;
  onRemove: () => void;
  onChange: (settings: Record<string, any>) => void;
  onOpenPicker: (type: 'image_picker' | 'product' | 'category', currentValue: string, onSelect: (val: string) => void) => void;
}) {
  const sortableId = `block:${sectionId}:${blockId}`;
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: sortableId, data: { type: 'block', sectionId } });
  const style = { transform: CSS.Transform.toString(transform), transition };
  
  const [isExpanded, setIsExpanded] = useState(true);

  if (!block) return null;
  const blockSchema = schema.blocks!.find(b => b.type === block.type);

  return (
    <div ref={setNodeRef} style={style} className="rounded-md border bg-slate-50/50 shadow-sm relative z-10 overflow-hidden">
      <div className="flex items-center justify-between p-2 bg-slate-100/80 border-b">
        <div className="flex items-center gap-2 cursor-pointer flex-1" onClick={() => setIsExpanded(!isExpanded)}>
          <div {...attributes} {...listeners} className="cursor-grab hover:text-primary p-1" onClick={e => e.stopPropagation()}>
            <GripVertical className="h-3 w-3 text-slate-400" />
          </div>
          <div className="text-xs font-semibold">{blockSchema?.name ?? block.type}</div>
        </div>
        <button onClick={onRemove} className="text-red-400 hover:text-red-600 p-1 rounded-sm hover:bg-red-50 transition-colors" title="Remove block">
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
      {isExpanded && (
        <div className="p-3">
          <SchemaFields
            settings={blockSchema?.settings ?? []}
            values={block.settings}
            onOpenPicker={onOpenPicker}
            onChange={onChange}
          />
        </div>
      )}
    </div>
  );
}

function SettingsPanel({
  storeTheme,
  settingsData,
  onChange,
  onOpenPicker,
}: {
  storeTheme: StoreTheme;
  settingsData: Record<string, any>;
  onChange: (settings: Record<string, any>) => void;
  onOpenPicker: (type: 'image_picker' | 'product' | 'category', currentValue: string, onSelect: (val: string) => void) => void;
}) {
  const settingsSchema = parseSettingsSchema(storeTheme.assets ?? []);
  return (
    <div className="space-y-4">
      {settingsSchema.length === 0 ? (
        <SchemaFields
          settings={[
            { id: 'primary_color', label: 'Primary color', type: 'color', default: '#16a34a' },
            { id: 'heading_font', label: 'Heading font', type: 'font_picker', default: 'Inter' },
          ]}
          values={settingsData}
          onChange={onChange}
          onOpenPicker={onOpenPicker}
        />
      ) : (
        settingsSchema.map((group) => (
          <div key={group.name} className="rounded-md border bg-background p-3">
            <div className="mb-3 text-sm font-medium">{group.name}</div>
            <SchemaFields settings={group.settings ?? []} values={settingsData} onChange={onChange} onOpenPicker={onOpenPicker} />
          </div>
        ))
      )}
    </div>
  );
}

function SchemaFields({ settings, values, onChange, onOpenPicker }: { settings: SchemaSetting[]; values: Record<string, any>; onChange: (values: Record<string, any>) => void; onOpenPicker: (type: 'image_picker' | 'product' | 'category', currentValue: string, onSelect: (val: string) => void) => void; }) {
  if (settings.length === 0) {
    return <div className="text-xs text-muted-foreground">No editable settings.</div>;
  }

  return (
    <div className="space-y-3">
      {settings.filter((setting) => setting.id).map((setting) => {
        const value = values[setting.id] ?? setting.default ?? '';
        const setValue = (next: any) => onChange({ ...values, [setting.id]: next });
        
        const isPicker = setting.type === 'image_picker' || setting.type === 'product' || setting.type === 'category';
        
        return (
          <label key={setting.id} className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">{setting.label ?? setting.id}</span>
            {setting.type === 'checkbox' ? (
              <input type="checkbox" checked={Boolean(value)} onChange={(e) => setValue(e.target.checked)} />
            ) : setting.type === 'select' ? (
              <select value={String(value)} onChange={(e) => setValue(e.target.value)} className="h-9 w-full rounded-md border bg-background px-3 text-sm">
                {(setting.options ?? []).map((option) => {
                  const normalized = typeof option === 'string' ? { label: option, value: option } : option;
                  return <option key={normalized.value} value={normalized.value}>{normalized.label ?? normalized.value}</option>;
                })}
              </select>
            ) : setting.type === 'font_picker' ? (
              <FontPicker value={String(value)} onChange={setValue} />
            ) : isPicker ? (
              <div className="flex flex-col gap-2">
                {value && setting.type === 'image_picker' && (
                  <div className="relative w-full aspect-video rounded-md overflow-hidden border bg-slate-100">
                    <img src={value} alt="Selected" className="w-full h-full object-contain" />
                  </div>
                )}
                {value && (setting.type === 'product' || setting.type === 'category') && (
                  <div className="text-xs font-mono bg-slate-100 p-2 rounded-md break-all">ID: {value}</div>
                )}
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => onOpenPicker(setting.type as 'image_picker' | 'product' | 'category', value, setValue)}
                >
                  {value ? 'Change' : 'Select'} {setting.type.replace('_picker', '')}
                </Button>
              </div>
            ) : (
              <Input
                type={setting.type === 'color' ? 'color' : setting.type === 'range' ? 'number' : 'text'}
                value={String(value)}
                onChange={(e) => setValue(e.target.value)}
              />
            )}
          </label>
        );
      })}
    </div>
  );
}

function TabButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: ReactNode; label: string }) {
  return (
    <button className={`flex flex-1 items-center justify-center gap-2 border-b-2 py-3 text-sm font-medium transition-colors ${active ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`} onClick={onClick}>
      {icon}
      {label}
    </button>
  );
}

function hydrateSections(sectionsData: any, sectionAssets: ThemeAsset[]): BuilderSection[] {
  if (sectionsData?.sections && Array.isArray(sectionsData.order)) {
    return sectionsData.order
      .map((id: string): BuilderSection | null => {
        const section = sectionsData.sections[id];
        return section ? { 
          id, 
          type: section.type, 
          settings: section.settings ?? {},
          blocks: section.blocks ?? {},
          block_order: section.block_order ?? []
        } : null;
      })
      .filter((section: BuilderSection | null): section is BuilderSection => section !== null);
  }

  return sectionAssets.slice(0, 4).map((asset) => {
    const type = sectionTypeFromKey(asset.key);
    return newSection(type, schemaFromAsset(asset));
  });
}

function newSection(type: string, schema: SectionSchema): BuilderSection {
  return {
    id: `${type}-${Date.now().toString(36)}`,
    type,
    settings: Object.fromEntries((schema.settings ?? []).filter((setting) => setting.id).map((setting) => [setting.id, setting.default ?? ''])),
    blocks: {},
    block_order: [],
  };
}

function sectionTypeFromKey(key: string) {
  return key.replace(/^sections\//, '').replace(/\.liquid$/, '');
}

function schemaFromAsset(asset: ThemeAsset): SectionSchema {
  return asset.sectionSchema && typeof asset.sectionSchema === 'object'
    ? asset.sectionSchema
    : { name: sectionTypeFromKey(asset.key), settings: [] };
}

function sectionSchemaFor(assets: ThemeAsset[], type: string) {
  return schemaFromAsset(assets.find((asset) => sectionTypeFromKey(asset.key) === type) ?? { id: type, key: `sections/${type}.liquid`, contentType: 'text/plain' });
}

function parseSettingsSchema(assets: ThemeAsset[]): Array<{ name: string; settings?: SchemaSetting[] }> {
  const asset = assets.find((item) => item.key === 'config/settings_schema.json');
  if (!asset?.content) return [];
  try {
    const parsed = JSON.parse(asset.content);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function templateOptions(storeTheme: StoreTheme) {
  const fromAssets = (storeTheme.assets ?? [])
    .filter((asset) => asset.key.startsWith('templates/') && asset.key.endsWith('.liquid'))
    .map((asset) => asset.key.replace(/^templates\//, '').replace(/\.liquid$/, ''));
  const fromSaved = (storeTheme.pageTemplates ?? []).map((template) => template.templateType);
  return [...new Set(['index', ...fromAssets, ...fromSaved])];
}
